import { z } from 'zod';
import { CalendarTimeZone } from './calendar.js';

const PositiveId = z.number().int().positive();
const UniqueIds = z
  .array(PositiveId)
  .max(5_000)
  .refine((ids) => new Set(ids).size === ids.length, 'ids must be unique');
const NumericKey = z.string().regex(/^\d+$/);

export const MailColumn = z.enum([
  'account',
  'sender',
  'subject',
  'snippet',
  'attachment',
  'date',
]);
export type MailColumn = z.infer<typeof MailColumn>;

export const DEFAULT_MAIL_COLUMN_ORDER: MailColumn[] = [
  'account',
  'sender',
  'subject',
  'snippet',
  'attachment',
  'date',
];

export const MailColumnWidths = z.object({
  account: z.number().int().min(64).max(240),
  sender: z.number().int().min(96).max(360),
  subject: z.number().int().min(140).max(600),
  snippet: z.number().int().min(120).max(700),
  attachment: z.number().int().min(28).max(64),
  date: z.number().int().min(60).max(160),
});
export type MailColumnWidths = z.infer<typeof MailColumnWidths>;

export const DEFAULT_MAIL_COLUMN_WIDTHS: MailColumnWidths = {
  account: 116,
  sender: 192,
  subject: 300,
  snippet: 360,
  attachment: 36,
  date: 84,
};

const MailColumnOrder = z
  .array(MailColumn)
  .length(DEFAULT_MAIL_COLUMN_ORDER.length)
  .refine(
    (columns) => new Set(columns).size === DEFAULT_MAIL_COLUMN_ORDER.length,
    'mail columns must be unique',
  );
export const HideableMailColumn = z.enum(['account', 'sender', 'snippet', 'attachment', 'date']);
export type HideableMailColumn = z.infer<typeof HideableMailColumn>;
const HiddenMailColumns = z
  .array(HideableMailColumn)
  .max(HideableMailColumn.options.length)
  .refine((columns) => new Set(columns).size === columns.length, 'hidden columns must be unique');

export const MailSenderSubjectLayout = z.enum(['auto', 'horizontal', 'stacked']);
export type MailSenderSubjectLayout = z.infer<typeof MailSenderSubjectLayout>;

export const RemoteImagePolicy = z.enum(['blocked', 'proxy', 'direct']);
export type RemoteImagePolicy = z.infer<typeof RemoteImagePolicy>;

export const SavedMailSearch = z.object({
  id: PositiveId,
  name: z.string().trim().min(1).max(80),
  query: z.string().trim().min(1).max(1_000),
  sort: z.enum(['relevance', 'newest']).default('newest'),
  pinned: z.boolean().default(true),
});
export type SavedMailSearch = z.infer<typeof SavedMailSearch>;

const SavedMailSearches = z
  .array(SavedMailSearch)
  .max(100)
  .refine(
    (searches) => new Set(searches.map((search) => search.id)).size === searches.length,
    'saved search ids must be unique',
  );

export const CalendarSchedulingPreferences = z.object({
  timeZone: CalendarTimeZone.nullable().default(null),
  weekdays: z
    .array(z.number().int().min(0).max(6))
    .min(1)
    .max(7)
    .refine((days) => new Set(days).size === days.length, 'weekdays must be unique')
    .default([1, 2, 3, 4, 5]),
  startMinutes: z.number().int().min(0).max(1_439).default(9 * 60),
  endMinutes: z.number().int().min(1).max(1_440).default(17 * 60),
  durationMinutes: z.number().int().min(15).max(480).default(30),
  bufferBeforeMinutes: z.number().int().min(0).max(240).default(0),
  bufferAfterMinutes: z.number().int().min(0).max(240).default(0),
  calendarIds: UniqueIds.default([]),
});
export type CalendarSchedulingPreferences = z.infer<
  typeof CalendarSchedulingPreferences
>;

export function defaultCalendarSchedulingPreferences(): CalendarSchedulingPreferences {
  return {
    timeZone: null,
    weekdays: [1, 2, 3, 4, 5],
    startMinutes: 9 * 60,
    endMinutes: 17 * 60,
    durationMinutes: 30,
    bufferBeforeMinutes: 0,
    bufferAfterMinutes: 0,
    calendarIds: [],
  };
}

export const DisplayPreferences = z.object({
  theme: z.enum(['system', 'light', 'dark']),
  navCollapsed: z.boolean(),
  defaultComposeAccountId: PositiveId.nullable().default(null),
  defaultCalendarId: PositiveId.nullable().default(null),
  calendarView: z.enum(['month', 'week', 'day']).default('month'),
  calendarShowTasks: z.boolean().default(true),
  calendarScheduling: CalendarSchedulingPreferences.default(
    defaultCalendarSchedulingPreferences(),
  ),
  plannerNativeNotifications: z.boolean().default(false),
  undoSendSeconds: z.number().int().min(0).max(30).default(5),
  savedMailSearches: SavedMailSearches.default([]),
  accountOrder: UniqueIds.default([]),
  calendarOrder: UniqueIds.default([]),
  collapsedAccountIds: UniqueIds,
  expandedMoreAccountIds: UniqueIds,
  folderOrderByAccount: z.record(NumericKey, UniqueIds),
  folderPlacementById: z.record(
    NumericKey,
    z.enum(['main', 'more', 'hidden']),
  ),
  accountIdentityMode: z.enum(['color', 'name', 'both']),
  mailReadingLayout: z.enum(['full', 'vertical', 'horizontal']).default('full'),
  mailListDensity: z.enum(['compact', 'comfortable', 'spacious']).default('comfortable'),
  mailSenderSubjectLayout: MailSenderSubjectLayout.default('auto'),
  remoteImagePolicy: RemoteImagePolicy.default('blocked'),
  mailColumnOrder: MailColumnOrder.default([...DEFAULT_MAIL_COLUMN_ORDER]),
  hiddenMailColumns: HiddenMailColumns.default([]),
  mailColumnWidths: MailColumnWidths.default({ ...DEFAULT_MAIL_COLUMN_WIDTHS }),
  verticalSplitPercent: z.number().int().min(30).max(70).default(42),
  horizontalSplitPercent: z.number().int().min(30).max(70).default(45),
});
export type DisplayPreferences = z.infer<typeof DisplayPreferences>;

export const DisplayPreferencesResponse = z.object({
  preferences: DisplayPreferences,
  updatedAt: z.number().int().nonnegative(),
});
export type DisplayPreferencesResponse = z.infer<typeof DisplayPreferencesResponse>;

export function defaultDisplayPreferences(): DisplayPreferences {
  return {
    theme: 'system',
    navCollapsed: false,
    defaultComposeAccountId: null,
    defaultCalendarId: null,
    calendarView: 'month',
    calendarShowTasks: true,
    calendarScheduling: defaultCalendarSchedulingPreferences(),
    plannerNativeNotifications: false,
    undoSendSeconds: 5,
    savedMailSearches: [],
    accountOrder: [],
    calendarOrder: [],
    collapsedAccountIds: [],
    expandedMoreAccountIds: [],
    folderOrderByAccount: {},
    folderPlacementById: {},
    accountIdentityMode: 'both',
    mailReadingLayout: 'full',
    mailListDensity: 'comfortable',
    mailSenderSubjectLayout: 'auto',
    remoteImagePolicy: 'blocked',
    mailColumnOrder: [...DEFAULT_MAIL_COLUMN_ORDER],
    hiddenMailColumns: [],
    mailColumnWidths: { ...DEFAULT_MAIL_COLUMN_WIDTHS },
    verticalSplitPercent: 42,
    horizontalSplitPercent: 45,
  };
}
